<template>
  <div>
    <div v-if="rows && rows.length === 0" class="text-center py-5">
      <i
        class="fe fe-inbox text-muted"
        style="font-size: 48px; opacity: 0.6"
      ></i>
      <p class="mt-3 text-muted">Tidak ada data untuk ditampilkan.</p>
    </div>
    <vue-good-table
      v-else
      :columns="columns"
      :rows="rows"
      :total-rows="total"
      :sort-options="{ enabled: true }"
      :pagination-options="{ enabled: true }"
      :select-options="{ enabled: false }"
      style-class="vgt-table border-0 table-auto"
      @on-sort-change="$emit('sort-change', $event)"
      @on-page-change="$emit('page-change', $event)"
      @on-per-page-change="$emit('per-page-change', $event)"
      styleClass="vgt-table condensed"
    >
      <template slot="table-column" slot-scope="props">
        <span v-if="props.column.field === '__select'" class="approval-checkbox-cell">
          <input
            type="checkbox"
            :checked="isAllActionableSelected"
            :indeterminate.prop="isSomeActionableSelected && !isAllActionableSelected"
            :disabled="actionableRows.length === 0"
            @change="toggleAllActionable($event.target.checked)"
          />
        </span>
        <span v-else>{{ props.column.label }}</span>
      </template>

      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === '__select'" class="approval-checkbox-cell">
          <input
            type="checkbox"
            :checked="isRowSelected(props.row)"
            :disabled="!isRowSelectable(props.row)"
            @change="toggleRowSelection(props.row, $event.target.checked)"
          />
        </span>

        <span
          v-if="props.column.field === 'action'"
          class="d-flex gap-3 justify-content-center align-items-center"
        >
          <b-button
            class="no-wrap btn-pill"
            variant="info"
            size="sm"
            v-b-tooltip.hover.left="'View'"
            @click="$emit('view-detail', props.row)"
          >
            <span class="fe fe-eye"></span>
          </b-button>
          <b-button
            v-if="learningType === 'experiential_learning' && props.row?.is_actionable !== false"
            class="no-wrap btn-pill"
            variant="warning"
            size="sm"
            v-b-tooltip.hover.left="'Feedback'"
            @click="$emit('feedback-project', props.row)"
          >
            <span class="fe fe-edit"></span>
          </b-button>
          <b-button
            v-if="learningType === 'experiential_learning' && props.row?.is_actionable !== false"
            class="no-wrap btn-pill"
            variant="success"
            size="sm"
            v-b-tooltip.hover.left="'Approve'"
            @click="$emit('approve-project', props.row)"
          >
            <span class="fe fe-check-circle"></span>
          </b-button>
          <b-button
            v-if="learningType === 'experiential_learning' && props.row?.is_actionable !== false"
            class="no-wrap btn-pill"
            variant="danger"
            size="sm"
            v-b-tooltip.hover.left="'Reject'"
            @click="$emit('reject-project', props.row)"
          >
            <span class="fe fe-x-circle"></span>
          </b-button>
        </span>

        <div
          v-if="props.column.field === 'level'"
          class="d-flex flex-column gap-1 justify-content-start align-items-start"
        >
          <div class="rectangle-container">
            <div
              v-for="n in 5"
              :key="'proficiency_standard-' + n"
              :class="[
                'rectangle',
                {
                  'filled-target':
                    n <= props.row?.competency?.proficiency_standard,
                },
              ]"
            ></div>
          </div>
          <div class="rectangle-container">
            <div
              v-for="n in 5"
              :key="'proficiency_standard-' + n"
              :class="[
                'rectangle',
                {
                  'filled-achievement': n <= props.row?.competency?.proficiency,
                },
              ]"
            ></div>
          </div>
        </div>
        <span
          v-if="props.column.field === 'gap'"
          class="d-flex gap-3 justify-content-start align-items-center"
        >
          <span
            :class="getGapBadgeClass(props.row)"
          >
            {{ formatCompetencyGap(props.row) }}
          </span>
        </span>

        <span v-if="props.column.field === 'status'">
          <div
            class="d-flex flex-row align-items-center justify-content-start"
            @click="$emit('change-status', props.row)"
          >
            <span :class="getTrainingStatusClass(props.row.status)">
              {{ snakeToTitle(props.row.status) }}
            </span>
          </div>
        </span>

        <span v-else-if="props.column.field === 'project.start_date'">
          <div class="d-flex flex-row align-items-center justify-content-start">
            <span>{{ formatDate(props.row?.project?.start_date) || "-" }}</span>
          </div>
        </span>

        <span v-else-if="props.column.field === 'project.end_date'">
          <div class="d-flex flex-row align-items-center justify-content-start">
            <span>{{ formatDate(props.row?.project?.end_date) || "-" }}</span>
          </div>
        </span>

        <span
          v-else-if="props.column.field === 'project.duration'"
          class="d-flex align-items-center justify-content-start"
        >
          {{ props.row?.project?.duration }} Days
        </span>

        <span v-else class="d-flex align-items-center justify-content-start">
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <template slot="pagination-bottom" slot-scope="props">
        <div
          class="d-flex justify-content-between align-items-center mt-3 px-3"
        >
          <div class="text-muted">
            Showing
            <strong>{{ showingFrom }} - {{ showingTo }}</strong>
            of <strong>{{ total }}</strong> items
          </div>

          <b-pagination
            v-model="page"
            :total-rows="total"
            :per-page="pageSize"
            first-number
            last-number
            align="right"
            size="md"
            @change="$emit('page-change-from-bv', $event)"
          />
        </div>
      </template>
    </vue-good-table>

    <div
      v-if="learningType !== 'experiential_learning'"
      class="d-flex justify-content-end mt-4"
    >
      <b-button
        v-if="
          learningType === 'social_learning' ||
          learningType === 'structured_learning'
        "
        class="mr-2"
        variant="light"
        :disabled="isApproveDisabled"
        @click="$emit('reject')"
      >
        Reject ({{ selectedRows.length }})
      </b-button>
      <b-button
        variant="primary"
        :disabled="isApproveDisabled"
        @click="$emit('approve')"
      >
        <template>
          <span class="fe fe-save"></span>
          Approve
        </template>
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  getTrainingStatusClass,
  snakeToTitle,
  formatDate,
} from "../utils/utils";
import { isApprovalActionable } from "@/utils/approvalStatus";
import { mapGetters } from "vuex";

export default {
  name: "EnrollmentReportTable",
  props: {
    rows: Array,
    total: Number,
    showingFrom: Number,
    showingTo: Number,
    page: Number,
    pageSize: Number,
    learningType: {
      type: String,
      default: "structured_learning",
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    isLoadingApprove: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters("users", ["profileData"]),
    currentUserRoles() {
      return this.profileData?.roles_uenroll || this.profileData?.roles || [];
    },
    isApproveDisabled() {
      return (
        !this.selectedRows ||
        this.selectedRows.length === 0 ||
        this.isLoadingApprove
      );
    },

    columns() {
      const baseColumns = (() => {
      switch (this.learningType) {
        case "social_learning":
          return this.socialColumns;
        case "experiential_learning":
          return this.experientialColumns;
        case "structured_learning":
        default:
          return this.structuredColumns;
      }
      })();

      if (this.learningType === "experiential_learning") return baseColumns;

      return [
        {
          label: "",
          field: "__select",
          sortable: false,
          width: "42px",
          thClass: "table-header text-center approval-checkbox-column",
          tdClass: "text-center approval-checkbox-column",
        },
        ...baseColumns,
      ];
    },

    actionableRows() {
      return (this.rows || []).filter((row) => this.isRowSelectable(row));
    },

    selectedRowKeys() {
      return new Set((this.selectedRows || []).map((row) => this.getRowKey(row)));
    },

    isAllActionableSelected() {
      return this.actionableRows.length > 0 && this.actionableRows.every((row) => this.selectedRowKeys.has(this.getRowKey(row)));
    },

    isSomeActionableSelected() {
      return this.actionableRows.some((row) => this.selectedRowKeys.has(this.getRowKey(row)));
    },
  },

  data() {
    return {
      structuredColumns: [
        {
          label: "Title",
          field: "training.title",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Competency",
          field: "competency.name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Level",
          field: "level",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Gap",
          field: "gap",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Platform",
          field: "training.platform",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Type",
          field: "training.type.name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "status",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
      socialColumns: [
        {
          label: "Title",
          field: "coaching.title",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Competency",
          field: "competency.name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Level",
          field: "level",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Gap",
          field: "gap",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Methodology",
          field: "coaching.methodology",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Coach / Mentor Name",
          field: "coaching.coach_name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "status",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
      experientialColumns: [
        {
          label: "Project",
          field: "project.name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Start Date",
          field: "project.start_date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "End Date",
          field: "project.end_date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Duration",
          field: "project.duration",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "status",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
    };
  },

  methods: {
    getTrainingStatusClass,
    snakeToTitle,
    formatDate,
    getRowKey(row) {
      return `${this.learningType}-${row?.id}`;
    },
    isRowSelectable(row) {
      if (this.learningType !== "structured_learning") {
        return row?.is_actionable !== false;
      }
      return isApprovalActionable(row, this.currentUserRoles);
    },
    isRowSelected(row) {
      return this.selectedRowKeys.has(this.getRowKey(row));
    },
    emitSelectedRows(rows) {
      this.$emit("select-change", { selectedRows: rows });
    },
    toggleRowSelection(row, checked) {
      if (!this.isRowSelectable(row)) return;

      const rowKey = this.getRowKey(row);
      const selectedRows = checked
        ? [...(this.selectedRows || []).filter((item) => this.getRowKey(item) !== rowKey), row]
        : (this.selectedRows || []).filter((item) => this.getRowKey(item) !== rowKey);

      this.emitSelectedRows(selectedRows);
    },
    toggleAllActionable(checked) {
      this.emitSelectedRows(checked ? [...this.actionableRows] : []);
    },
    getCompetencyGap(row) {
      const standard = Number(row?.competency?.proficiency_standard);
      const actual = Number(row?.competency?.proficiency);

      if (Number.isNaN(standard) || Number.isNaN(actual)) return null;

      return actual - standard;
    },
    formatCompetencyGap(row) {
      const gap = this.getCompetencyGap(row);

      if (gap === null) return "-";
      if (gap > 0) return `+${gap}`;

      return String(gap);
    },
    getGapBadgeClass(row) {
      const gap = this.getCompetencyGap(row);

      if (gap === null) return "badge bg-light text-muted";
      return gap >= 0 ? "badge bg-success" : "badge bg-danger";
    },
    approve() {
      this.$emit("approve");
    },
  },
};
</script>

<style scoped>
.w-action {
  width: 140px;
  min-width: 140px;
  max-width: 140px;
}

.approval-checkbox-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.approval-checkbox-cell input[type="checkbox"] {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: #dc3545;
}

.approval-checkbox-cell input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

:deep(.approval-checkbox-column) {
  width: 42px !important;
  min-width: 42px !important;
  max-width: 42px !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.vgt-table.table-auto table {
  table-layout: auto !important;
}

:deep(.custom-switch .custom-control-label) {
  display: flex !important;
  align-items: center !important;
  cursor: pointer;
}

:deep(
    .custom-switch
      .custom-control-input:not(:checked)
      ~ .custom-control-label::before
  ) {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
}

:deep(
    .custom-switch .custom-control-input:checked ~ .custom-control-label::before
  ) {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

:deep(
    .custom-switch .custom-control-input:checked ~ .custom-control-label::after
  ) {
  background-color: #fff !important;
}

:deep(.table-header) {
  font-size: 12px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 12px !important;
}

:deep(.vgt-table) {
  border: 1px solid #e6ebf3 !important;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.vgt-table thead th) {
  background: #f8fafd !important;
  color: #8aa0c7 !important;
  padding: 12px 10px !important;
  text-transform: none;
}

:deep(.vgt-table tbody td) {
  border-color: #edf1f7 !important;
  padding: 10px !important;
  vertical-align: middle !important;
}

:deep(.vgt-table tbody tr:hover) {
  background: #fffafa !important;
}

:deep(.badge) {
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
  padding: 4px 8px;
}

:deep(.btn-pill.btn-sm) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  padding: 0;
  border-radius: 6px;
}

:deep(.vgt-left-align) {
  align-content: center !important;
}
</style>
